import { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  Plus,
  Check,
  LogOut,
  X,
  Mail,
  Globe,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useAuthStore } from "../../stores/authStore";
import EmailSpinner from "./EmailSpinner";

export default function AccountSwitcher() {
  const { user, accounts, switchAccount, addAccount, removeAccount, logoutAll } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const modalBackdropRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showAddModal) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 50);
    }
  }, [showAddModal]);

  if (!user) return null;

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newPassword.trim()) return;

    setIsLoading(true);
    setErrorMsg(null);
    try {
      await addAccount(newEmail.trim(), newPassword.trim());
      setShowAddModal(false);
      setNewEmail("");
      setNewPassword("");
      setShowNewPassword(false);
      setIsOpen(false);
    } catch (err: any) {
      setErrorMsg(err.message || "Error al autenticar el buzón");
    } finally {
      setIsLoading(false);
    }
  };

  const domainName = user.domain || user.email.split("@")[1] || "MagicMail";
  const userInitial = (user.displayName || user.email).charAt(0).toUpperCase();

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Top Header Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors group"
        title="Cambiar de bandeja o dominio"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-6 h-6 bg-accent flex items-center justify-center text-white text-2xs font-bold flex-shrink-0">
            {userInitial}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-[var(--text-primary)] truncate flex items-center gap-1.5 leading-tight">
              <span className="text-accent">{domainName}</span>
              {accounts.length > 1 && (
                <span className="text-[10px] bg-accent/15 text-accent px-1 font-mono font-semibold">
                  {accounts.length}
                </span>
              )}
            </p>
            <p className="text-2xs text-[var(--text-muted)] truncate leading-tight mt-0.5">
              {user.email}
            </p>
          </div>
        </div>

        <ChevronDown
          size={14}
          className={`text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "rotate-180 text-accent" : ""
          }`}
        />
      </button>

      {/* Accounts Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl z-50 py-1 animate-in fade-in-0 zoom-in-95 duration-100">
          <div className="px-3 py-1.5 border-b border-[var(--border-subtle)] text-[10px] uppercase font-bold text-[var(--text-muted)] tracking-wider flex items-center justify-between">
            <span>Bandejas Vinculadas</span>
            <span className="text-accent font-mono">{accounts.length}</span>
          </div>

          {/* Account list */}
          <div className="max-h-64 overflow-y-auto divide-y divide-[var(--border-subtle)]/40">
            {accounts.map((acc) => {
              const isActive = acc.email.toLowerCase() === user.email.toLowerCase();
              const accDomain = acc.domain || acc.email.split("@")[1] || "";
              const initial = (acc.displayName || acc.email).charAt(0).toUpperCase();

              return (
                <div
                  key={acc.email}
                  className={`flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-secondary)] transition-colors group cursor-pointer ${
                    isActive ? "bg-accent/10 font-medium" : ""
                  }`}
                  onClick={() => {
                    switchAccount(acc.email);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div
                      className={`w-6 h-6 text-2xs font-bold flex items-center justify-center flex-shrink-0 ${
                        isActive
                          ? "bg-accent text-white"
                          : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border border-[var(--border-subtle)]"
                      }`}
                    >
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[var(--text-primary)] truncate font-medium">
                        {accDomain}
                      </p>
                      <p className="text-2xs text-[var(--text-muted)] truncate">
                        {acc.email}
                      </p>
                    </div>
                  </div>

                  {isActive ? (
                    <Check size={14} className="text-accent flex-shrink-0" />
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAccount(acc.email);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-[var(--text-muted)] hover:text-red-500 transition-opacity"
                      title="Desvincular bandeja"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border-t border-[var(--border-subtle)] my-1" />

          {/* Compact icon-only actions */}
          <div className="flex items-center justify-center gap-1 px-2 py-1">
            <button
              onClick={() => {
                setShowAddModal(true);
                setIsOpen(false);
              }}
              className="p-1.5 text-[var(--text-muted)] hover:text-accent hover:bg-accent/10 transition-colors"
              title="Vincular otra bandeja..."
            >
              <Plus size={14} />
            </button>

            {accounts.length > 1 && (
              <button
                onClick={() => {
                  logoutAll();
                  setIsOpen(false);
                }}
                className="p-1.5 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                title="Cerrar todas las sesiones"
              >
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal to Add Account rendered in global body portal */}
      {showAddModal &&
        createPortal(
          <div
            ref={modalBackdropRef}
            className="dialog-backdrop fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 p-4 animate-in fade-in-0 duration-150"
            onMouseDown={(e) => {
              if (e.target === modalBackdropRef.current) setShowAddModal(false);
            }}
          >
            <div className="dialog-panel w-[460px] max-w-[94vw] flex flex-col bg-[var(--bg-primary)] border border-[var(--border-subtle)] shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <div className="flex items-center gap-2">
                  <Globe size={14} className="text-accent" />
                  <h2 className="text-xs font-bold uppercase tracking-wider text-[var(--text-primary)]">
                    Vincular Bandeja de Otro Dominio
                  </h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleAddSubmit} className="p-5 space-y-4">
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  Ingresa las credenciales del correo que deseas vincular (ej. <code className="font-mono text-accent">carlos@r-z.xyz</code>). Podrás alternar entre tus bandejas en 1 clic sin tener que salir ni iniciar sesión nuevamente.
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                      Correo Electrónico
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input
                        ref={emailInputRef}
                        type="email"
                        required
                        placeholder="usuario@tudominio.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-xs bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-accent outline-none font-mono transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                      Contraseña del Buzón
                    </label>
                    <div className="relative flex items-center">
                      <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        required
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-9 pr-9 py-2 text-xs bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-accent outline-none font-mono transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2.5 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                        title={showNewPassword ? "Ocultar contraseña" : "Ver contraseña"}
                        aria-label={showNewPassword ? "Ocultar contraseña" : "Ver contraseña"}
                      >
                        {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <div className="p-2.5 bg-red-500/10 border-l-2 border-l-red-500 text-red-500 text-xs">
                    {errorMsg}
                  </div>
                )}

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border-subtle)]">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-3.5 py-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] border border-[var(--border-subtle)] transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-1.5 text-xs font-semibold bg-accent text-white hover:bg-accent-hover active:scale-[0.98] transition-all flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {isLoading ? <EmailSpinner size={14} /> : <Plus size={13} />}
                    {isLoading ? "Autenticando..." : "Vincular Bandeja"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
